import React, { useState, useEffect } from 'react';
import { useAutosave } from '../../../hooks/useAutosave';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Save, Plus, Trash2, Star, CheckCircle, Send, Mail, Clock, X, Copy, FileSignature, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PolicyDeliveryForm = ({ caseId, delivery, caseInfo, agent, onUpdate }) => {
  const [data, setData] = useState(delivery || {});
  const [saving, setSaving] = useState(false);
  useAutosave(() => save(), data);

  useEffect(() => {
    if (!delivery && caseInfo) {
      setData({
        carrier: caseInfo.carrier,
        coverage_amount: caseInfo.coverage_amount,
        premium: caseInfo.premium,
        delivery_method: 'in_person'
      });
    } else {
      setData(delivery || {});
    }
  }, [delivery, caseInfo]);

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/compliance/cases/${caseId}/policy-delivery?advisor_id=${agent.id}&advisor_name=${encodeURIComponent(agent.name)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      onUpdate();
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="delivery-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Policy Delivery Receipt</CardTitle>
        <Button onClick={save} disabled={saving} className="bg-[#0077B6]" data-testid="save-delivery-btn">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div><label className="block text-sm font-medium mb-1">Policy Number</label>
            <input value={data.policy_number || ''} onChange={(e) => setData({ ...data, policy_number: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="del-policy-number" /></div>
          <div><label className="block text-sm font-medium mb-1">Carrier</label>
            <input value={data.carrier || ''} onChange={(e) => setData({ ...data, carrier: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="del-carrier" /></div>
          <div><label className="block text-sm font-medium mb-1">Product Name</label>
            <input value={data.product_name || ''} onChange={(e) => setData({ ...data, product_name: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="del-product" /></div>
          <div><label className="block text-sm font-medium mb-1">Coverage Amount ($)</label>
            <input type="number" value={data.coverage_amount || ''} onChange={(e) => setData({ ...data, coverage_amount: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg" data-testid="del-coverage" /></div>
          <div><label className="block text-sm font-medium mb-1">Premium ($)</label>
            <input type="number" step="0.01" value={data.premium || ''} onChange={(e) => setData({ ...data, premium: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg" data-testid="del-premium" /></div>
          <div><label className="block text-sm font-medium mb-1">Issue Date</label>
            <input type="date" value={data.issue_date || ''} onChange={(e) => setData({ ...data, issue_date: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="del-issue-date" /></div>
          <div><label className="block text-sm font-medium mb-1">Delivery Date</label>
            <input type="date" value={data.delivery_date || ''} onChange={(e) => setData({ ...data, delivery_date: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="del-delivery-date" /></div>
          <div><label className="block text-sm font-medium mb-1">Delivery Method</label>
            <select value={data.delivery_method || 'in_person'} onChange={(e) => setData({ ...data, delivery_method: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="del-method">
              <option value="in_person">In Person</option>
              <option value="courier">Courier</option>
              <option value="email">Email</option>
              <option value="mail">Mail</option>
            </select></div>
        </div>

        <div className="flex flex-col gap-2 pt-3 border-t">
          {[
            ['policy_reviewed', 'Policy reviewed with client'],
            ['premium_confirmed', 'Premium confirmed'],
            ['beneficiary_confirmed', 'Beneficiary designation confirmed'],
            ['exclusions_reviewed', 'Exclusions/Limitations reviewed'],
            ['free_look_reviewed', 'Free-look period reviewed'],
            ['client_accepted', 'Client accepted policy'],
            ['client_signature', 'Client Signature obtained'],
            ['advisor_signature', 'Advisor Signature obtained'],
          ].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2">
              <input type="checkbox" checked={data[k] || false} onChange={(e) => setData({ ...data, [k]: e.target.checked })} data-testid={`del-${k}`} />
              <span className="text-sm">{l}</span>
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================
// Supervisor Review (Admin)
// =============================================================
